import React, { useState, useEffect } from 'react';
import { reportingAPI } from '../services/api';
import toast from 'react-hot-toast';

const ReportViewer = ({ reportId, onClose }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (reportId) {
      fetchReport();
    }
  }, [reportId]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await reportingAPI.getReport(reportId);
      setReport(response.data);
    } catch (err) {
      console.error('Error fetching report:', err);
      setError('Failed to load report');
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'generating': return 'text-yellow-600 bg-yellow-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getReportTypeColor = (type) => {
    switch (type) {
      case 'assessment': return 'text-blue-600 bg-blue-100';
      case 'prescreening': return 'text-purple-600 bg-purple-100';
      case 'impression': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading report...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Report</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={fetchReport}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Retry
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!report) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {report.title || 'Report Viewer'}
            </h2>
            <div className="flex items-center gap-4 mt-2">
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(report.status)}`}>
                {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
              </span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${getReportTypeColor(report.report_type)}`}>
                {report.report_type.charAt(0).toUpperCase() + report.report_type.slice(1)}
              </span>
              <span className="text-sm text-gray-500">
                Generated: {formatDate(report.generated_at)}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {report.status === 'generating' ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Generating Report</h3>
              <p className="text-gray-600">Your report is being generated. This may take a few minutes.</p>
              <button
                onClick={fetchReport}
                className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Refresh Status
              </button>
            </div>
          ) : report.status === 'failed' ? (
            <div className="text-center py-12">
              <div className="text-red-500 text-6xl mb-4">❌</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Report Generation Failed</h3>
              <p className="text-gray-600 mb-4">There was an error generating your report. Please try again.</p>
              <button
                onClick={fetchReport}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : report.content ? (
            <div className="space-y-6">
              {/* Executive Summary */}
              {report.content.executive_summary && (
                <div className="bg-gradient-to-r from-primary-50 to-white p-6 rounded-lg border border-primary-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Executive Summary</h3>
                  <p className="text-gray-700 leading-relaxed">{report.content.executive_summary}</p>
                </div>
              )}

              {/* Overall Score */}
              {report.content.overall_score && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Overall Score</h3>
                  <div className="flex items-center gap-4">
                    <div className="text-4xl font-bold text-primary-600">
                      {Math.round(report.content.overall_score)}%
                    </div>
                    <div className="flex-1">
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div 
                          className="bg-primary-600 h-3 rounded-full transition-all duration-500"
                          style={{ width: `${report.content.overall_score}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Strengths and Weaknesses */}
              <div className="grid md:grid-cols-2 gap-6">
                {report.content.strengths && report.content.strengths.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-green-800 mb-3">Strengths</h3>
                    <ul className="space-y-2">
                      {report.content.strengths.map((strength, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-green-600 mt-1">✓</span>
                          <span className="text-green-700">{strength}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {report.content.weaknesses && report.content.weaknesses.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-red-800 mb-3">Areas for Improvement</h3>
                    <ul className="space-y-2">
                      {report.content.weaknesses.map((weakness, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-red-600 mt-1">•</span>
                          <span className="text-red-700">{weakness}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Report Sections */}
              {report.content.sections && report.content.sections.length > 0 && (
                <div className="space-y-6">
                  <h3 className="text-xl font-semibold text-gray-900">Detailed Analysis</h3>
                  {report.content.sections.map((section, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">{section.title}</h4>
                      <div className="prose max-w-none">
                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{section.content}</p>
                      </div>
                      {section.confidence_score && (
                        <div className="mt-3 text-sm text-gray-500">
                          Confidence Score: {Math.round(section.confidence_score * 100)}%
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Insights */}
              {report.content.insights && report.content.insights.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-blue-800 mb-4">Key Insights</h3>
                  <div className="grid gap-4">
                    {report.content.insights.map((insight, index) => (
                      <div key={index} className="bg-white p-4 rounded-lg border border-blue-100">
                        <div className="flex items-start gap-3">
                          <div className={`w-3 h-3 rounded-full mt-2 ${
                            insight.insight_type === 'strength' ? 'bg-green-500' :
                            insight.insight_type === 'weakness' ? 'bg-red-500' :
                            insight.insight_type === 'opportunity' ? 'bg-blue-500' :
                            'bg-yellow-500'
                          }`}></div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">{insight.title}</h4>
                            <p className="text-gray-700 mt-1">{insight.description}</p>
                            {insight.confidence_score && (
                              <div className="mt-2 text-sm text-gray-500">
                                Confidence: {Math.round(insight.confidence_score * 100)}%
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {report.content.recommendations && report.content.recommendations.length > 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-3">Recommendations</h3>
                  <ul className="space-y-2">
                    {report.content.recommendations.map((recommendation, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-yellow-600 mt-1">💡</span>
                        <span className="text-yellow-700">{recommendation}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Career Recommendations */}
              {report.content.career_recommendations && report.content.career_recommendations.length > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-purple-800 mb-3">Career Recommendations</h3>
                  <ul className="space-y-2">
                    {report.content.career_recommendations.map((recommendation, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="text-purple-600 mt-1">🎯</span>
                        <span className="text-purple-700">{recommendation}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 text-6xl mb-4">📄</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Content Available</h3>
              <p className="text-gray-600">This report doesn't have any content yet.</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              Report ID: {report.id}
            </div>
            <div className="flex gap-3">
              <button
                onClick={fetchReport}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Refresh
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportViewer;



